import { useEffect } from 'react';
import type { LiveStore } from './createLiveStore';

/**
 * Repaint a live table as soon as its tab becomes visible again.
 *
 * The store stops scheduling notification flushes while `document.hidden`, so a
 * hidden tab keeps its data current but runs no ROW renders. Coming back, the
 * accumulated listeners need one flush; otherwise the table would keep painting
 * the state it had when it was hidden until the next frame happened to arrive —
 * which, on a quiet market, can be a long time.
 *
 * Scope, precisely: this only covers what flows through the store's `notify`.
 * The page tree above the rows still reconciles per socket message, because
 * `useSocketEvent` bumps a `useState` on every frame it delivers and the tab
 * components are not memoized. A hidden tab is therefore NOT idle. See
 * `arbitrage-core/docs/specs/opener-socket-event-rerender.md`.
 *
 * The DOM listener lives here rather than inside the store because the store
 * has no teardown hook to unregister it; a `useEffect` cleanup does.
 */
export function useFlushOnVisible<T>(store: LiveStore<T> | null | undefined) {
  useEffect(() => {
    if (!store) return;
    if (typeof document === 'undefined') return;

    const onVisibilityChange = () => {
      if (!document.hidden) store.flushPending();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [store]);
}
